import {
    DefaultError,
    DefaultOptions,
    QueryClient,
    QueryObserverResult,
    UseMutationOptions,
    UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { every, find, some } from 'lodash';

const queryConfig: DefaultOptions = {
    queries: {
        refetchOnWindowFocus: false,
        retry: false,
    },
};

export const queryClient = new QueryClient({
    defaultOptions: queryConfig,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BroadArgsFunction = (...args: any) => any;

export type ExtractFnReturnType<FnType extends BroadArgsFunction> = Awaited<ReturnType<FnType>>;

/**
 * `TData` is what the caller reads, which is the query function's return unless `select` narrows or
 * reshapes it. A hook exposing the parameter lets its caller pass a `select` that changes the type;
 * the default keeps every other call site inferring exactly what it did before.
 *
 * `DefaultError` rather than a concrete error type: the app registers no global error type with
 * react-query, so this is the `TError` react-query already infers everywhere.
 */
export type QueryConfig<QueryFnType extends BroadArgsFunction, TData = ExtractFnReturnType<QueryFnType>> = Omit<
    UseQueryOptions<ExtractFnReturnType<QueryFnType>, DefaultError, TData>,
    'queryKey' | 'queryFn'
>;

/**
 * A query result stitched together from more than one underlying query.
 *
 * `refetch` and `promise` are dropped because there is no honest single-query answer for them: taken
 * from one of the underlying queries, either would silently cover only part of the result.
 *
 * Narrow rather than `Omit`, because the producing hooks have to read each field off react-query's
 * result to build this: that proxy records which properties were touched and re-renders the consumer
 * when any of them changes. Copying the whole object would subscribe every chart to `dataUpdatedAt`,
 * `errorUpdateCount` and the rest of the bookkeeping. Add a field here when something needs it.
 *
 * `isPlaceholderData` earns its subscription: it flips only when a placeholder is installed or replaced,
 * and both of those already change `data` and `isFetching`, so it costs no render that was not happening
 * anyway. It is the only honest answer to "does the data I hold describe the range I asked for", which a
 * caller cannot derive from `data` alone once the placeholder has been narrowed to a subset of that range.
 */
export type CombinedQueryResult<TData> = Pick<
    QueryObserverResult<TData>,
    | 'data'
    | 'error'
    | 'status'
    | 'isPending'
    | 'isLoading'
    | 'isError'
    | 'isSuccess'
    | 'isFetching'
    | 'isPlaceholderData'
>;

/**
 * A useQueries result stitched together from more than one underlying query (but still segmented by the outer consumer's query list)
 */
export type CombinedQueriesResult<TData> = CombinedQueryResult<(TData | undefined)[]>;

/**
 * A type that may be either a CombinedQueryResult or CombinedQueriesResult, but with the status fields factored out into the container
 * to make type checking work more easily for a return value that varies only in `data`. This is a helper for combination spotterId/spotterIds
 * query hooks - generally this type shouldn't be exposed to callers, instead, use overloads to discriminate whether you're returning a
 * CombinedQueryResult or CombinedQueriesResult depending on the spotterId(s) parameter passed in (see {@link useDetections}).
 */
export type CombinedQueryOrQueriesResult<TData> = CombinedQueryResult<TData | (TData | undefined)[]>;

export type SpotterIdOrIds =
    | { spotterId?: string | null; spotterIds?: never }
    | { spotterId?: never; spotterIds?: string[] };

/**
 * {@link SpotterIdOrIds} after a wrapper has destructured it, which TypeScript cannot put back together
 * into the union.
 *
 * Both keys are required, so only a caller that really is handing both through reaches an overload
 * taking this. A caller naming one spotter parameter still resolves against the union.
 */
export type DestructuredSpotterIdOrIds = {
    spotterId: string | null | undefined;
    spotterIds: string[] | undefined;
};

/** Everything a {@link CombinedQueryResult} carries apart from the data itself. */
export type QueryStatusFields = Omit<CombinedQueryResult<unknown>, 'data'>;

/**
 * Copies the status fields off a query result, leaving `data` to the caller: a hook combining queries
 * has its own answer for that, and no general one exists.
 */
export const pickQueryStatus = (result: QueryStatusFields): QueryStatusFields => ({
    error: result.error,
    status: result.status,
    isPending: result.isPending,
    isLoading: result.isLoading,
    isError: result.isError,
    isSuccess: result.isSuccess,
    isFetching: result.isFetching,
    isPlaceholderData: result.isPlaceholderData,
});

/**
 * The verdict for a result whose own inputs are still unresolved, so none of its queries exists yet.
 *
 * One shared object, so a hook waiting on its parameters hands out an unchanged status and its callers'
 * memos hold their identity.
 */
export const PENDING_STATUS: QueryStatusFields = {
    status: 'pending',
    isPending: true,
    isLoading: false,
    isError: false,
    isSuccess: false,
    isFetching: false,
    isPlaceholderData: false,
    error: null,
};

/**
 * One verdict over the parts a combined result is built from.
 *
 * A part is pending, fetching, erroring or standing in with placeholder data if any of them is, and
 * successful only once all of them are. `every` over an empty list is `true`, so a result built from
 * nothing at all is successfully empty; a caller whose parts do not exist yet wants
 * {@link PENDING_STATUS} instead.
 *
 * The parts are whatever the caller treats as one unit, which need not be single queries: a hook running
 * several windows per spotter rolls each spotter's windows up first, so one spotter carrying a placeholder
 * does not read as the whole result being fresh.
 */
export const rollUpStatus = (parts: QueryStatusFields[]): QueryStatusFields => {
    const isPending = some(parts, 'isPending');
    const isError = some(parts, 'isError');
    const isSuccess = every(parts, 'isSuccess');

    return {
        status: isSuccess ? 'success' : isPending ? 'pending' : 'error',
        isPending,
        isError,
        isSuccess,
        isLoading: some(parts, 'isLoading'),
        isFetching: some(parts, 'isFetching'),
        isPlaceholderData: some(parts, 'isPlaceholderData'),
        error: find(parts, 'error')?.error ?? null,
    };
};

export type MutationConfig<MutationFnType extends BroadArgsFunction> = UseMutationOptions<
    ExtractFnReturnType<MutationFnType>,
    AxiosError,
    Parameters<MutationFnType>[0]
>;
